import React from 'react';

import * as Sentry from '@sentry/react';
import amplitude from 'amplitude-js';
import SameCardLinkedModal from 'apps/auth/components/cards/SameCardLinkedModal';
import { DangerouslyHtml } from 'apps/auth/components/common/DangerouslyHtml/DangerouslyHtml';
import { InfoPage } from 'apps/auth/components/common/Layout/InfoPage/InfoPage';
import PopupButton from 'apps/auth/components/common/Popup/PopupButton';
import SimplePopup from 'apps/auth/components/common/Popup/SimplePopup';
import CvvPopup from 'apps/auth/components/common/popups/CvvPopup';
import PreCheckPopup from 'apps/auth/components/common/send/popups/Precheck/PreCheckPopup';
import { getEmptyNotValidFields, getSortFields, validationAndUpdateValue } from 'apps/auth/components/form-multi/utils';
import Disclaimer from 'apps/auth/components/send/disclaimers/Disclaimer';
import OnboardingQuestionnaireModal from 'apps/auth/components/send/modals/OnboardingQuestionnaireModal';
import SdkModals from 'apps/auth/components/send/SdkModals';
import {
    CALCULATOR_SCREEN,
    SENDER_ADDITIONAL_INFO_SCREEN,
    PAYMENT_METHOD_SCREEN,
    RECIPIENT_MAIN_INFO_SCREEN,
    RECIPIENT_ADDITIONAL_INFO_SCREEN,
    CARD_EXTRA_FIELDS_SCREEN,
    TRANSFER_ADDITIONAL_INFO_SCREEN,
} from 'apps/auth/components/send/SendConstants';
import { transactionMethods } from 'apps/auth/components/utils/AmplitudeUtils';
import ModalComponent from 'apps/auth/components/utils/ModalComponent';
import ServerErrorPopup from 'apps/auth/components/utils/ServerErrorPopup';
import { NEW_EXTERNAL_CARD_ADDED } from 'apps/auth/constants/amplitude/AmplitudeEvents';
import { PAYMENT_FLOW, PAYMENT_METHOD_AMPLITUDE, PAYOUT_COUNTRY, PAYOUT_METHOD } from 'apps/auth/constants/amplitude/AmplitudeParamName';
import {
    BLOCK_THREE_MONTH_LIMIT_ISSUE,
    DC,
    DELIVERY_BANK_CARD,
    DELIVERY_SWIFT,
    ERROR,
    ERROR_TOAST,
    MAX_AMOUNT_ISSUE,
    PAYMENT_ACH,
    PAYMENT_BANK_CARD,
    PAYMENT_BANK_CARD_110,
    PAYMENT_DEBIT_BANK_CARD_111,
    PAYMENT_CREDIT_BANK_CARD_112,
    PAYMENT_FLINKS,
    PAYMENT_LEAN,
    PAYMENT_MODULR,
    PAYMENT_OFAKIM,
    PAYMENT_PLAID,
    RECIPIENT_TYPE_SELF,
    USA_PROFILE_LIMIT_ISSUE,
} from 'apps/auth/constants/Constants';
import {
    AMOUNT_FROM,
    AMOUNT_TO,
    AMOUNT_TO_CCY,
    BANK_ACCOUNT_CODE,
    BANK_ACCOUNTCODE,
    DELIVERY_METHOD,
    FROM_ACCOUNT,
    IS_IBAN,
    PAYMENT_METHOD,
    REPEAT_PAYMENT_ID,
    SOURCE_CARD_CVV,
    SOURCE_CODE_FROM,
    SWIFT_CODE,
    TO_ACCOUNT_TYPE,
    TO_BIC,
    TO_IBAN,
    TO_ISSUER_CODE,
    TO_NAME,
    TO_NAME_F,
    TO_RECIPIENT,
} from 'apps/auth/constants/fieldsName';
import { CVV, EXP_DATE, PAN, SCREEN_HEIGHT, SCREEN_WIDTH } from 'apps/auth/constants/FieldType';
import { SEND_FORM_NAME } from 'apps/auth/constants/formName';
import { RECIPIENT_ADDITIONAL_INFO, RECIPIENT_MAIN_INFO, SENDER_ADDITIONAL_INFO, TRANSFER_ADDITIONAL_INFO } from 'apps/auth/constants/send/FieldCategories';
import { AUTH_CANCEL_TRANSACTION, AUTH_SEND_PRECHECK } from 'apps/auth/constants/UrlConstants';
import { apiFetchData } from 'apps/auth/helpers/api';
import { logWarn } from 'apps/auth/helpers/clientLogger';
import { getExtra, isSoftOrHardServerError, resolveArguments, showErrorType } from 'apps/auth/helpers/utils';

import { ApiService } from './apiService';
import { usePayee } from './payeeServices';

type AmplitudeInstance = {
    logEvent: (event: string, payload?: Record<string, unknown>) => void;
};
const amplitudeInstance = amplitude as { getInstance: () => AmplitudeInstance };

type FieldValue = string | number | boolean | null | undefined;
type FieldError = { code?: string | number; body?: string };
type FormField = {
    name?: string;
    value?: FieldValue;
    visible?: boolean;
    destination?: string;
    type?: string;
    category?: string;
    forExtraScreen?: boolean;
    nonRepeatable?: boolean;
    nonRepeatableFill?: boolean;
    order?: number;
    mandatory?: boolean;
    showOnAmountMore?: boolean;
    highRiskField?: unknown;
    isReadOnly?: boolean;
    isError?: boolean;
    error?: FieldError;
    errorMessage?: string;
    comment?: string;
    commentLong?: string;
    [key: string]: unknown;
};
type FieldsMap = Record<string, FormField>;
type FieldList = FormField[];
type FieldsCollection = FieldsMap | FieldList;
type ErrorFields = Array<{ name: string }> | Record<string, FieldError>;
type ApiErrors = Record<string, FieldError>;
type PaymentMethodDetails = {
    method?: string;
    code?: string;
    lastSuccess?: boolean;
    defaultPaymentSource?: boolean;
};
type PaymentMethodsList = Record<string, PaymentMethodDetails>;
type ApiPrecheck = { id?: string | number; deliveryMethod?: string; paymentMethod?: string };
type AmountRestriction = {
    multiple?: number | null;
    [key: string]: unknown;
};
type ApiExtra = {
    sendFlowV3?: boolean;
    plaidAccountAction?: boolean;
    canShowAmlLevelUpgrade?: boolean;
    canShowOowFlow?: boolean;
    needSecretQuestionsCheck?: boolean;
    leanAppToken?: string;
    leanCustomerId?: string;
    show_modal_kyc_increase_limit?: boolean;
    ofakimFlowUpdateProfile?: boolean;
    ofakimHandleKycFlow?: boolean;
    canUseBankTransferMethod?: boolean;
    banks?: unknown[];
    flink?: boolean;
    fieldsForShow?: string[];
    specialValidationFlow?: boolean;
    recipientSaved?: boolean;
    precheck?: ApiPrecheck;
    additionalLimitError?: boolean;
    textFirstPopup?: string;
    serverErrorCode?: { code?: number | null };
    aml_level_form?: { fields?: FieldsMap };
    profile?: { userName?: string; userLastName?: string; fullName?: string };
    toCurrency?: { id?: FieldValue };
    toCountryId?: { name?: string; value?: FieldValue };
    isDisclaimerFromServerSide?: boolean;
    showPaymentMethodRestriction?: boolean;
    increaseLimit?: string;
    canIncreaseLimit?: boolean;
    aml_gt_increase_level_link?: string;
    availablePaymentMethodsList?: PaymentMethodsList;
    paymentMethodsOrder?: {
        sourceOptionsGroups?: Record<string, { paymentOptionsItems?: Array<{ category?: string }> }>;
    };
    commission?: {
        restrictionFrom?: AmountRestriction;
        restrictionTo?: AmountRestriction;
        [key: string]: unknown;
    };
    [key: string]: unknown;
};
type ApiResponse = {
    errors?: ApiErrors;
    extra?: ApiExtra;
    forms?: Record<string, { fields: FieldsMap }>;
    messages?: Record<string, string>;
    messageBodyMessages?: Array<{ messageType?: string }>;
    redirectTo?: string;
    showServerError?: boolean;
    app?: { messageBody?: unknown[] };
};

type SubmitRef = React.RefObject<HTMLButtonElement | HTMLInputElement | { click: () => void }>;
type PopupRef = React.RefObject<{ open: (...args: any[]) => void; close: () => void }>;
type ModalRef = React.RefObject<{ open: (...args: any[]) => void }>;
type ControllerRef = React.MutableRefObject<AbortController | null> | { current?: AbortController | null } | null;
type SendParamToStateMachine = (event: { type: string; data?: Record<string, unknown> }) => void;

type UpdateFormProperties = (formName: string, actionUrl: string | null, submit?: boolean) => Promise<unknown>;
type ChangeFormFieldValue = (formName: string, value: FieldValue, fieldName: string) => void;
type ChangeFormField = (formName: string, field: FormField) => void;
type UpdateFields = (formName: string, fields: FieldsMap) => void;
type SetNewFields = (formName: string, fields: FieldsMap) => Promise<unknown>;
type SetFieldError = (formName: string, fieldName: string, error?: FieldError) => void;
type ScrollToField = (formName: string, fieldName: string) => void;
type SetNewForm = (formName: string, fields: FieldsMap, action: string) => void;
type SetFieldVisible = (formName: string, fieldName: string, visible: boolean) => void;
type ChangeFieldProperty = (formName: string, fieldName: string, propertyName: string, value: unknown) => void;
type RemoveFields = (formName: string, fieldNames: string[]) => void;

type ToggleSpinner = (value: boolean) => Promise<unknown> | void;
type ToggleModal = (...args: unknown[]) => void;
type UpdateMessages = (messages: unknown) => void;
type UpdatePageMessages = (messages: Record<string, string>) => void;
type AddExtras = (extra: ApiExtra) => Promise<unknown> | void;

type PaymentMethodServiceRef = React.RefObject<{ changePaymentMethod: (method: string, code: string, field: unknown) => void }>;
type PayeeProps = Parameters<typeof usePayee>[4];
type ScreenValidationProps = PayeeProps & {
    changeFormField: ChangeFormField;
    toggleSpinner: (value: boolean) => void;
    toggleModalWithTimer: (...args: any[]) => void;
};

const goToScreenWithError = (
    sendParamToStateMachine: SendParamToStateMachine,
    fields: FieldsMap,
    errorFields: ErrorFields,
    currentScreen: string,
    bonusWithdrawFlow: boolean,
) => {
    const keys: string[] = [];
    if (Array.isArray(errorFields)) {
        errorFields.forEach((f) => {
            if (f.name) {
                keys.push(f.name);
            }
        });
    } else {
        keys.push(...Object.keys(errorFields));
    }
    const amountToName = fields[AMOUNT_TO]?.name;
    const amountFromName = fields[AMOUNT_FROM]?.name;
    const hasErrorOnCalculator = !!keys.find((k) => k === amountFromName || k === amountToName);

    const hasErrorOnRecipientMainFields = !!keys.find(
        (k) => fields[k]?.destination === 'TO' && !fields[k]?.forExtraScreen && !(fields[k]?.category === 'transfer_additional_info'),
    );
    const hasErrorOnRecipientAdditionalFields = !!keys.find(
        (k) => (fields[k]?.destination === 'TO' && fields[k]?.forExtraScreen && !fields[k].category) || fields[k]?.category === RECIPIENT_ADDITIONAL_INFO,
    );

    const hasErrorOnSenderAdditionalFields = !!keys.find((k) => {
        const f = fields[k];
        return (
            (f?.destination === 'FROM' && f?.type !== PAN && f?.type !== CVV && f?.type !== EXP_DATE && !fields[k].category) ||
            fields[k]?.category === SENDER_ADDITIONAL_INFO
        );
    });

    const hasErrorOnTransferAdditionalFields = !!keys.find((k) => fields[k]?.category === TRANSFER_ADDITIONAL_INFO);
    const hasErrorOnCardExtraFields = !!keys.find(() => {});

    const fieldsExist = !!keys.find((k) => fields[k]);

    if (fieldsExist) {
        sendParamToStateMachine({
            type: 'GO_TO_SCREEN_WITH_PRECHECK_ERROR',
            data: {
                hasErrorOnCalculator: hasErrorOnCalculator,
                bonusWithdrawFlow: bonusWithdrawFlow,
                hasErrorOnRecipientMainFields: hasErrorOnRecipientMainFields,
                hasErrorOnRecipientAdditionalFields: hasErrorOnRecipientAdditionalFields,
                hasErrorOnSenderAdditionalFields: hasErrorOnSenderAdditionalFields,
                hasErrorOnTransferAdditionalFields: hasErrorOnTransferAdditionalFields,
                hasErrorOnCardExtraFields: hasErrorOnCardExtraFields,
            },
        });
    }
};

const getSenderAdditionalOrCardFields = (fields: FieldsMap): FormField[] =>
    Object.values(fields).filter((f) => f.visible && (f.destination === 'FROM' || f.category === SENDER_ADDITIONAL_INFO));

const getCardFields = (fields: FieldList): FormField[] => Object.values(fields).filter((f) => f.type === PAN || f.type === EXP_DATE);

const getSortedFields = (fields: FieldsCollection): FormField[] => getSortFields(fields) as FormField[];

export const showSenderAdditionalScreen = (fields: FieldsMap): boolean => {
    const senderAdditionalOrCardsExtraFields = getSenderAdditionalOrCardFields(fields);
    const cardFields = getCardFields(senderAdditionalOrCardsExtraFields);
    const isPaymentMethodBankCard =
        fields[PAYMENT_METHOD]?.value === PAYMENT_BANK_CARD ||
        fields[PAYMENT_METHOD]?.value === PAYMENT_BANK_CARD_110 ||
        fields[PAYMENT_METHOD]?.value === PAYMENT_DEBIT_BANK_CARD_111 ||
        fields[PAYMENT_METHOD]?.value === PAYMENT_CREDIT_BANK_CARD_112;
    if (isPaymentMethodBankCard) {
        return cardFields.length === 0 || senderAdditionalOrCardsExtraFields.length > cardFields.length;
    } else {
        return cardFields.length === 0 && senderAdditionalOrCardsExtraFields.length > cardFields.length;
    }
};

export const getCardScreenFields = (fields: FieldsCollection, extras: Record<string, unknown> = {}): FormField[] => {
    const visibleFields: FormField[] = [];
    const sortedFields = getSortedFields(fields);
    sortedFields.forEach((field) => {
        const isFromField = field.visible && field.destination === 'FROM';
        let isCardField = field.type === PAN || field.type === CVV || field.type === EXP_DATE;
        if (extras?.sendFlowV3) {
            isCardField = field.type === PAN || field.type === EXP_DATE;
        }
        if (isFromField && isCardField) {
            visibleFields.push(field);
        }
    });
    return visibleFields;
};

export const getSenderAdditionalFields = (fields: FieldsCollection): FormField[] => {
    const visibleFields: FormField[] = [];
    const sortedFields = getSortedFields(fields);
    sortedFields.forEach((field) => {
        const defaultFieldCondition =
            field.visible && field.destination === 'FROM' && field.category !== RECIPIENT_ADDITIONAL_INFO && field.category !== TRANSFER_ADDITIONAL_INFO;
        const isFieldWithSpecialCategory = field.visible && field.category === SENDER_ADDITIONAL_INFO;

        const isCardField = field.type === PAN || field.type === CVV || field.type === EXP_DATE;
        if ((defaultFieldCondition || isFieldWithSpecialCategory) && !isCardField) {
            visibleFields.push(field);
        }
    });
    return visibleFields;
};

/**
 * https://paysend.atlassian.net/browse/PBTC-1166
 */
const hardCodeOffshoreAsia = (fields: FieldList, fieldName?: string): boolean => {
    if (fields && fieldName === 'to_bank_name_other') {
        const bunkNameNotFound = fields.find((field) => field?.type === 'drop_box' && field?.value === 'UNLISTED_BANK');
        return bunkNameNotFound !== undefined;
    }
    return true;
};

export const getRecipientAdditionalFields = (fields: FieldsCollection): FormField[] => {
    const visibleFields: FormField[] = [];
    const sortedFields = getSortedFields(fields);
    sortedFields.forEach((field) => {
        const isFieldWithSpecialCategory = field.visible && field.category === RECIPIENT_ADDITIONAL_INFO;
        const defaultFieldCondition =
            field.visible &&
            field.destination === 'TO' &&
            field.forExtraScreen &&
            field.category !== TRANSFER_ADDITIONAL_INFO &&
            field.category !== SENDER_ADDITIONAL_INFO;
        if (isFieldWithSpecialCategory || defaultFieldCondition) {
            visibleFields.push(field);
        }
    });
    return visibleFields;
};

export const getTransferAdditionalFields = (fields: FieldsCollection): FormField[] => {
    const visibleFields: FormField[] = [];
    const sortedFields = getSortedFields(fields);
    sortedFields.forEach((field) => {
        if (field.visible && field.category === TRANSFER_ADDITIONAL_INFO) {
            visibleFields.push(field);
        }
    });
    return visibleFields;
};

const getRecipientMainDetailsFields = (fields: FieldsCollection, fieldsForShow?: string[]): FormField[] => {
    const visibleFields: FormField[] = [];
    const sortedFields = getSortedFields(fields);
    sortedFields.forEach((field) => {
        const visibleField = fieldsForShow?.find((fieldName) => fieldName === field.name);
        if (
            (field.visible &&
                field.category !== TRANSFER_ADDITIONAL_INFO &&
                field.category !== RECIPIENT_ADDITIONAL_INFO &&
                field.category !== SENDER_ADDITIONAL_INFO &&
                field.highRiskField == null &&
                field.destination === 'TO' &&
                hardCodeOffshoreAsia(sortedFields, field.name)) ||
            visibleField ||
            (field.visible && field.category === RECIPIENT_MAIN_INFO)
        ) {
            visibleFields.push(field);
        }
    });
    return visibleFields;
};

type AdditionalInfo = {
    senderAdditionalOrCardsExtraFields: FormField[];
    recipientAdditionalFields: FormField[];
    transferAdditionalFields: FormField[];
    hasSenderAdditionalOrCardsExtraFields: boolean;
    hasRecipientAdditionalFields: boolean;
    hasTransferAdditionalFields: boolean;
    isShow: boolean;
    isShowCardExtraFields: boolean;
    isShowVisibleSenderAdditionalFields: boolean;
    isShowSenderAdditionalScreen: boolean;
    onlyCVV: boolean;
    hasSdkModal: boolean;
    modals: {
        isAch: boolean;
        isLean: boolean;
        isOfakim: boolean;
        isPlaid: boolean;
        isFlinks: boolean;
        isOfakimHandle: boolean;
    };
    isShowTransferAdditionalFields?: boolean;
};

export const getAdditionalInfo = (responseFields?: FieldsMap, extra?: ApiExtra): AdditionalInfo => {
    const result: AdditionalInfo = {
        senderAdditionalOrCardsExtraFields: [],
        recipientAdditionalFields: [],
        transferAdditionalFields: [],
        hasSenderAdditionalOrCardsExtraFields: false,
        hasRecipientAdditionalFields: false,
        hasTransferAdditionalFields: false,
        isShow: false,
        isShowCardExtraFields: false,
        isShowVisibleSenderAdditionalFields: false,
        isShowSenderAdditionalScreen: false,
        onlyCVV: false,
        hasSdkModal: false,
        modals: {
            isAch: false,
            isLean: false,
            isOfakim: false,
            isPlaid: false,
            isFlinks: false,
            isOfakimHandle: false,
        },
    };
    if (responseFields) {
        const sortedFields = getSortedFields(responseFields);
        const isRepeatPayment = sortedFields.some((f) => f?.name === REPEAT_PAYMENT_ID && f.value !== null && f.value !== '');
        for (const field of sortedFields) {
            if (!field.visible) continue;

            if (
                field.destination === 'TO' &&
                field.forExtraScreen &&
                field.category !== TRANSFER_ADDITIONAL_INFO &&
                field.category !== SENDER_ADDITIONAL_INFO
            ) {
                result.recipientAdditionalFields.push(field);
            } else if (
                (field.destination === 'FROM' && !isRepeatPayment) ||
                (field.destination === 'FROM' &&
                    isRepeatPayment &&
                    (field.nonRepeatable === true ||
                        field.nonRepeatableFill === true ||
                        field.type === PAN ||
                        field.type === CVV ||
                        field.type === EXP_DATE ||
                        !field.value)) ||
                field.category === SENDER_ADDITIONAL_INFO
            ) {
                result.senderAdditionalOrCardsExtraFields.push(field);
            } else if (field.category === TRANSFER_ADDITIONAL_INFO) {
                result.transferAdditionalFields.push(field);
            }
        }
        result.hasSenderAdditionalOrCardsExtraFields = result.senderAdditionalOrCardsExtraFields.length > 0;
        result.hasRecipientAdditionalFields = result.recipientAdditionalFields.length > 0;
        result.hasTransferAdditionalFields = result.transferAdditionalFields.length > 0;
        const visibleCardExtraFields = GTSendMultiScreenService.getVisibleFields(result.senderAdditionalOrCardsExtraFields, CARD_EXTRA_FIELDS_SCREEN);
        const visibleSenderAdditionalFields = GTSendMultiScreenService.getVisibleFields(
            result.senderAdditionalOrCardsExtraFields,
            SENDER_ADDITIONAL_INFO_SCREEN,
        );
        const visibleTransferAdditionalFields = GTSendMultiScreenService.getVisibleFields(result.transferAdditionalFields, TRANSFER_ADDITIONAL_INFO_SCREEN);
        result.isShowVisibleSenderAdditionalFields = visibleSenderAdditionalFields.length > 0;
        result.isShowTransferAdditionalFields = visibleTransferAdditionalFields.length > 0;
        result.isShowSenderAdditionalScreen = showSenderAdditionalScreen(responseFields);
        result.onlyCVV = visibleCardExtraFields.length === 1 && visibleCardExtraFields[0]?.type === CVV;
        result.isShowCardExtraFields = visibleCardExtraFields.length > 0 && !result.onlyCVV;

        result.isShow =
            result.isShowCardExtraFields ||
            result.isShowVisibleSenderAdditionalFields ||
            result.hasRecipientAdditionalFields ||
            result.hasTransferAdditionalFields;
    }
    if (extra) {
        const paymentMethod = responseFields?.[PAYMENT_METHOD]?.value;
        const isAch = Boolean(
            (extra.plaidAccountAction || extra.canShowAmlLevelUpgrade || extra.canShowOowFlow || extra.needSecretQuestionsCheck) &&
            paymentMethod === PAYMENT_ACH,
        );
        const isLean = Boolean(extra.leanAppToken && extra.leanCustomerId && paymentMethod === PAYMENT_LEAN);
        const isOfakim = Boolean(
            (extra.show_modal_kyc_increase_limit || extra.ofakimFlowUpdateProfile) && paymentMethod === PAYMENT_OFAKIM && !!extra.ofakimHandleKycFlow === false,
        );
        const isOfakimHandle = Boolean(
            (extra.show_modal_kyc_increase_limit || extra.ofakimFlowUpdateProfile) && paymentMethod === PAYMENT_OFAKIM && !!extra.ofakimHandleKycFlow,
        );
        const isPlaid = Boolean(extra.canUseBankTransferMethod && Array.isArray(extra.banks) && extra.banks.length > 0 && paymentMethod === PAYMENT_PLAID);
        const isFlinks = Boolean(extra.flink && paymentMethod === PAYMENT_FLINKS);
        result.modals = {
            isAch,
            isFlinks,
            isPlaid,
            isOfakim,
            isLean,
            isOfakimHandle,
        };
        result.hasSdkModal = isAch || isFlinks || isPlaid || isOfakim || isOfakimHandle || isLean;
    }

    return result;
};

export const hasExceedLimitErrors = (fields: FieldsMap = {}): boolean => {
    const fromField = fields[AMOUNT_FROM];
    const toField = fields[AMOUNT_TO];
    if (fromField?.isError || toField?.isError) {
        const error: Record<string | number, boolean> = {
            [MAX_AMOUNT_ISSUE]: true,
            [BLOCK_THREE_MONTH_LIMIT_ISSUE]: true,
            [USA_PROFILE_LIMIT_ISSUE]: true,
        };
        return Boolean((fromField?.error?.code && error[fromField.error.code]) || (toField?.error?.code && error[toField.error.code]));
    }
    return false;
};

export class GTSendMultiScreenService {
    #submitRef: SubmitRef;
    #paymentMethodService: PaymentMethodServiceRef;
    #extra?: ApiExtra;
    #ballId?: string | number;
    #isBonusWithdrawFlow = false;
    #isGlobalFlow: boolean;
    #isRepeat: boolean;
    #historyIndex: number;
    #typeOfTransfer: string;
    #sendUriPostfix: string;

    #toggleModal?: ToggleModal;
    #toggleSpinner?: ToggleSpinner;
    #updateMessages?: UpdateMessages;

    #addExtras?: AddExtras;
    #updatePageMessages?: UpdatePageMessages;

    #updateFormProperties?: UpdateFormProperties;
    #sendParamToStateMachine: SendParamToStateMachine;
    #isCurrencyChange = false;
    #changeFormFieldValue?: ChangeFormFieldValue;
    #updateFields?: UpdateFields;
    #setNewFields?: SetNewFields;
    #setFieldError?: SetFieldError;
    #scrollToField?: ScrollToField;
    #setNewForm?: SetNewForm;
    #setFieldVisible?: SetFieldVisible;
    #changeFieldProperty?: ChangeFieldProperty;
    #removeFields?: RemoveFields;

    //popups
    #popupRef?: PopupRef;
    #fieldsForShow?: string[];

    //modals
    #modalRef?: ModalRef;

    constructor(
        submitRef: SubmitRef,
        paymentMethodService: PaymentMethodServiceRef,
        isBonusWithdrawFlow: boolean,
        isGlobalFlow = false,
        isRepeat = false,
        historyIndex = 0,
        typeOfTransfer = '',
        ballId: string | number | undefined,
        send: SendParamToStateMachine,
    ) {
        this.#submitRef = submitRef;
        this.#paymentMethodService = paymentMethodService;
        this.#isBonusWithdrawFlow = isBonusWithdrawFlow;
        this.#isGlobalFlow = isGlobalFlow;
        this.#isRepeat = isRepeat;
        this.#historyIndex = historyIndex;
        this.#typeOfTransfer = typeOfTransfer;
        this.#sendParamToStateMachine = send;
        this.#sendUriPostfix = typeOfTransfer ? `?type=${typeOfTransfer}` : '';
        this.#ballId = ballId;
    }

    static getVisibleFields(fields: FieldsCollection, currentScreen: string, fieldsForShow: string[] = [], extras: Record<string, unknown> = {}): FormField[] {
        let screenFields: FormField[] = [];
        const fieldsMap = fields as FieldsMap;
        if (currentScreen === RECIPIENT_MAIN_INFO_SCREEN) {
            screenFields = getRecipientMainDetailsFields(fields, fieldsForShow);
        } else if (currentScreen === CALCULATOR_SCREEN) {
            screenFields.push(fieldsMap[AMOUNT_FROM]);
            screenFields.push(fieldsMap[AMOUNT_TO]);
        } else if (currentScreen === SENDER_ADDITIONAL_INFO_SCREEN) {
            screenFields = getSenderAdditionalFields(fields);
        } else if (currentScreen === RECIPIENT_ADDITIONAL_INFO_SCREEN) {
            screenFields = getRecipientAdditionalFields(fields);
        } else if (currentScreen === TRANSFER_ADDITIONAL_INFO_SCREEN) {
            screenFields = getTransferAdditionalFields(fields);
        } else if (currentScreen === CARD_EXTRA_FIELDS_SCREEN) {
            screenFields = getCardScreenFields(fields, extras);
        }
        return screenFields;
    }

    static toggleContinueButton(
        fields: FieldsMap,
        currentScreen: string,
        toggleContinueButton: (disabled: boolean) => void,
        binInfoExtra: Record<string, unknown> | null = null,
        isGlobalFlow = false,
        extras: Record<string, unknown> = {},
    ): void {
        let disabled = getEmptyNotValidFields(this.getVisibleFields(fields, currentScreen, [], extras), true, true)?.length > 0;
        const cardDeliveryMethod = fields.deliveryMethod.value === DELIVERY_BANK_CARD;
        if (currentScreen === CARD_EXTRA_FIELDS_SCREEN && !disabled && binInfoExtra?.error != null) {
            disabled = true;
        } else if (currentScreen === RECIPIENT_MAIN_INFO_SCREEN && cardDeliveryMethod && isGlobalFlow && (!binInfoExtra || binInfoExtra.error)) {
            disabled = true;
        } else if (currentScreen === CALCULATOR_SCREEN && disabled && hasExceedLimitErrors(fields)) {
            disabled = false;
        }
        toggleContinueButton(disabled);
    }

    setPopups(popupRef: PopupRef): void {
        this.#popupRef = popupRef;
    }

    setModals(modalRef: ModalRef): void {
        this.#modalRef = modalRef;
    }

    setExtra(extra: ApiExtra): void {
        this.#extra = extra;
    }

    setTypeOfTransfer(typeOfTransfer: string): void {
        this.#typeOfTransfer = typeOfTransfer;
        this.#sendUriPostfix = typeOfTransfer ? `?type=${typeOfTransfer}` : '';
    }

    setExtraAction(addExtras: AddExtras): void {
        this.#addExtras = addExtras;
    }

    setUpdatePageMessagesAction(updatePageMessages: UpdatePageMessages): void {
        this.#updatePageMessages = updatePageMessages;
    }

    setAppActions(toggleModal: ToggleModal, toggleSpinner: ToggleSpinner, updateMessages: UpdateMessages): void {
        this.#toggleModal = toggleModal;
        this.#toggleSpinner = toggleSpinner;
        this.#updateMessages = updateMessages;
    }

    toggleCvvPopup(
        cards: Array<{ code?: string; alias?: string; description?: string }>,
        cvvPopupTitle: string,
        sourceCodeFrom: string,
        messageBody: unknown[] = [],
        errors: ApiErrors = {},
        getPaymentFlow: () => string,
    ): void {
        const card = cards.find(({ code }) => code === sourceCodeFrom);
        const errorType = showErrorType(messageBody as Parameters<typeof showErrorType>[0], errors as Parameters<typeof showErrorType>[1]);
        if (!errorType) {
            this.#popupRef?.current?.open(
                <CvvPopup
                    formName={SEND_FORM_NAME}
                    title={resolveArguments(cvvPopupTitle, `${card?.alias} ·· ${card?.description}`)}
                    popupRef={this.#popupRef}
                    submitRef={this.#submitRef}
                    getPaymentFlow={getPaymentFlow}
                />,
            );
        }
    }

    rateFirstSubmitHandler(
        cards: Array<{ code?: string; alias?: string; description?: string }>,
        fields: FieldsMap,
        messages: Record<string, string>,
        json: ApiResponse,
        currentScreen: string,
        getPaymentFlow: () => string,
        availablePaymentMethodsList: PaymentMethodsList,
        paymentMethodState: unknown,
    ): void {
        this.#submitHandler(cards, fields, messages, json, currentScreen, false, undefined, getPaymentFlow, availablePaymentMethodsList, paymentMethodState);
    }

    #agreeWithDisclaimer(e: React.SyntheticEvent, submitForm = false): void {
        this.#updateFormActionUrl(e, `${AUTH_SEND_PRECHECK}?agreeDisclaimer=true&type=${this.#typeOfTransfer}`, submitForm);
    }

    #updateFormActionUrl(_e: React.SyntheticEvent, url: string, submitForm = false): void {
        void this.#updateFormProperties!(SEND_FORM_NAME, url, true).then(() => {
            this.#toggleModal!(false);
            if (submitForm) {
                this.#submitRef?.current?.click();
            }
        });
    }

    regularSubmitHandler(
        cards: Array<{ code?: string; alias?: string; description?: string }>,
        fields: FieldsMap,
        messages: Record<string, string>,
        json: ApiResponse,
        currentScreen: string,
        toCountry: { nameEn?: string } | string,
        getPaymentFlow: () => string,
        availablePaymentMethodsList: PaymentMethodsList,
        paymentMethodState: unknown,
    ): void {
        this.#submitHandler(cards, fields, messages, json, currentScreen, true, toCountry, getPaymentFlow, availablePaymentMethodsList, paymentMethodState);
    }

    setFormActions(
        updateFormProperties: UpdateFormProperties,
        changeFormFieldValue: ChangeFormFieldValue,
        updateFields: UpdateFields,
        setNewFields: SetNewFields,
        setFieldError: SetFieldError,
        scrollToField: ScrollToField,
        setNewForm: SetNewForm,
        setFieldVisible: SetFieldVisible,
        changeFieldProperty: ChangeFieldProperty,
        removeFields: RemoveFields,
    ): void {
        this.#updateFormProperties = updateFormProperties;
        this.#changeFormFieldValue = changeFormFieldValue;
        this.#updateFields = updateFields;
        this.#setNewFields = setNewFields;
        this.#setFieldError = setFieldError;
        this.#scrollToField = scrollToField;
        this.#setNewForm = setNewForm;
        this.#setFieldVisible = setFieldVisible;
        this.#changeFieldProperty = changeFieldProperty;
        this.#removeFields = removeFields;
    }

    getFieldsForShow(): string[] | undefined {
        return this.#fieldsForShow;
    }

    screenValidation(
        url: string,
        props: ScreenValidationProps,
        fields: FieldsMap,
        messages: Record<string, string>,
        _isHighRisk: boolean,
        controllerRef: ControllerRef,
        switchToNextScreen: () => void,
        currentScreen: string,
        resetViewRatesFirst: () => void,
        keepSpinner: boolean,
        hideRecipientSaved: () => boolean,
        incompleteTransfer: boolean,
        incompleteTransferValidationStep: string | number | undefined,
        typeOfTransfer: string,
        availablePaymentMethodsList: PaymentMethodsList = {},
        paymentMethodState: unknown,
        isViewRatesFirstFlow = false,
    ): void {
        const formName = SEND_FORM_NAME;
        if (currentScreen === RECIPIENT_MAIN_INFO_SCREEN) {
            url += '&saveRecipient=true';

            if (incompleteTransfer) {
                url += '&isIncomplete=true';
            }

            if (incompleteTransferValidationStep) {
                url += `&incompleteTransferValidationStep=${incompleteTransferValidationStep}`;
            }

            if (typeOfTransfer) {
                url += `&type=${typeOfTransfer}`;
            }
        }

        const screensToAddTransferType = [
            CARD_EXTRA_FIELDS_SCREEN,
            SENDER_ADDITIONAL_INFO_SCREEN,
            RECIPIENT_ADDITIONAL_INFO_SCREEN,
            TRANSFER_ADDITIONAL_INFO_SCREEN,
        ];
        let showFieldsWithAmountMore = false;
        if (screensToAddTransferType.includes(currentScreen)) {
            if (typeOfTransfer) {
                url += `&type=${typeOfTransfer}`;
            }
            if (CARD_EXTRA_FIELDS_SCREEN) {
                url += `&isCardScreen=true`;
            }
        }
        Object.keys(fields).map((key) => {
            const field = fields[key];
            if (field.visible && field.value && typeof field.value === 'string') {
                // clear leading space before validation and sendings
                field.value = field.value.trimStart();
                void validationAndUpdateValue(formName, field.value, field, props.changeFormField);
            }
            // showOnAmountMore бек не проверяет и мы можем получить пречек без них, поля эти в канаде
            if (field.forExtraScreen && field.showOnAmountMore && field.mandatory && field.visible && !field.value) {
                showFieldsWithAmountMore = true;
            }
        });
        void props.toggleSpinner(true);
        const apiFields = fields as Record<string, { name: string; canonicalPath?: string | null; value?: string | number | boolean }>;
        void apiFetchData<ApiResponse>(url, apiFields, controllerRef)
            .then((json) => {
                const fieldsForShow = Array.isArray(json?.extra?.fieldsForShow) ? (json.extra?.fieldsForShow ?? []) : [];
                const specialValidationFlow = Boolean(json?.extra?.specialValidationFlow);
                const showHiddenFields = (this.#isGlobalFlow || specialValidationFlow) && fieldsForShow.length > 0;
                if (json?.extra) {
                    void this.#addExtras!(json.extra);
                }
                this.#checkMessageBodyMessages(json);
                const recipientSaved = Boolean(json?.extra?.recipientSaved);
                const extra: ApiExtra = json.extra ?? {};
                const form = json.forms?.[SEND_FORM_NAME];
                const responseFields = form?.fields;
                const additionalInfo = getAdditionalInfo(responseFields, extra);
                const paymentMethodValue = responseFields?.[PAYMENT_METHOD]?.value;
                const paymentMethod = typeof paymentMethodValue === 'string' ? paymentMethodValue : '';
                const isShowSdkModal = additionalInfo.hasSdkModal;
                if (json.messages) {
                    this.#updatePageMessages!(json.messages);
                }
                const isShowAdditionalScreens = additionalInfo.isShow;
                const isPreCheck = !!extra?.precheck && !extra?.flink && !showFieldsWithAmountMore && (additionalInfo.onlyCVV || !isShowAdditionalScreens);
                const hasAmountFormatRestriction = extra.commission?.restrictionTo?.multiple > 0 || extra.commission?.restrictionFrom?.multiple > 0;
                if (fields[SCREEN_HEIGHT] && fields[SCREEN_WIDTH]) {
                    if (__isBrowser__) {
                        this.#changeFormFieldValue!(formName, window?.screen?.height, SCREEN_HEIGHT);
                        this.#changeFormFieldValue!(formName, window?.screen?.width, SCREEN_WIDTH);
                    }
                }
                if (recipientSaved && !hideRecipientSaved()) {
                    props.toggleModalWithTimer(true, messages?.recipientAdded);
                }
                if (
                    responseFields &&
                    responseFields[AMOUNT_FROM] &&
                    responseFields[AMOUNT_TO] &&
                    !extra?.precheck &&
                    !isViewRatesFirstFlow &&
                    !hasAmountFormatRestriction
                ) {
                    this.#changeFormFieldValue!(formName, responseFields[AMOUNT_FROM].value, AMOUNT_FROM);
                    this.#changeFormFieldValue!(formName, responseFields[AMOUNT_TO].value, AMOUNT_TO);
                }
                if (json?.redirectTo) {
                    props.toggleSpinner(true);
                    window.location.href = json.redirectTo;
                } else if (json?.showServerError && !isSoftOrHardServerError(json.extra?.serverErrorCode?.code)) {
                    this.#popupRef?.current?.open(
                        <ServerErrorPopup popupRef={this.#popupRef} errorText={json.messages?.errorText} buttonClose={json.messages?.buttonClose} />,
                    );
                } else if (json?.errors && Object.keys(json.errors).length > 0) {
                    this.#updatePageMessages!(json.messages ?? {});
                    const errorFields = GTSendMultiScreenService.getVisibleFields(fields, currentScreen, [], json.extra).filter(
                        (f) => json.errors?.[f.name ?? ''],
                    );
                    const hasError = errorFields?.length !== 0;
                    if (hasError) {
                        errorFields.forEach((f) => {
                            if (f.isReadOnly) {
                                f.isReadOnly = false;
                                f.isError = true;
                                f.errorMessage = json.errors?.[f.name ?? '']?.body;
                                props.changeFormField(formName, f);
                            } else {
                                this.#setFieldError!(formName, f.name ?? '', json.errors?.[f.name ?? '']);
                            }
                        });
                        this.#scrollToField!(formName, errorFields[0].name ?? '');
                        keepSpinner = false;
                        if (json?.errors[FROM_ACCOUNT]?.code === 315) {
                            this.#modalRef?.current?.open(<SameCardLinkedModal modalRef={this.#modalRef} />);
                        }
                    } else if (!hasError && json.extra?.additionalLimitError) {
                        keepSpinner = false;
                        resetViewRatesFirst();
                    } else if (!hasError && json.extra?.textFirstPopup) {
                        usePayee(json, undefined, formName, true, props, this.#popupRef, resetViewRatesFirst, this.#sendParamToStateMachine);
                        keepSpinner = false;
                    } else if (!hasError && showHiddenFields) {
                        this.#updateReduxForGlobalFlow(json, fields, fieldsForShow, formName, typeOfTransfer);
                        keepSpinner = false;
                    } else if (
                        !!extra?.precheck &&
                        !additionalInfo.onlyCVV &&
                        !additionalInfo.hasSdkModal &&
                        ((currentScreen === RECIPIENT_ADDITIONAL_INFO_SCREEN && !additionalInfo.hasTransferAdditionalFields) ||
                            (currentScreen === SENDER_ADDITIONAL_INFO_SCREEN &&
                                !additionalInfo.hasRecipientAdditionalFields &&
                                !additionalInfo.hasTransferAdditionalFields))
                    ) {
                        this.#sendNextWithCardModalIfNeeded(currentScreen, props, json.messages, extra, availablePaymentMethodsList, paymentMethodState);
                    } else if (isPreCheck) {
                        this.#sendNextWithCardModalIfNeeded(currentScreen, props, json.messages, extra, availablePaymentMethodsList, paymentMethodState);
                    } else {
                        switchToNextScreen();
                    }
                } else if (json.extra?.textFirstPopup) {
                    usePayee(json, undefined, formName, true, props, this.#popupRef, resetViewRatesFirst, this.#sendParamToStateMachine);
                    keepSpinner = false;
                } else if (showHiddenFields) {
                    this.#updateReduxForGlobalFlow(json, fields, fieldsForShow, formName, typeOfTransfer);
                } else if (isShowSdkModal && !isShowAdditionalScreens) {
                    if (additionalInfo.modals.isAch) {
                        const fieldsAch: FieldsMap = extra?.aml_level_form?.fields ?? {};
                        const jsonFields = responseFields ?? {};
                        void this.#setNewFields!(SEND_FORM_NAME, { ...jsonFields, ...fieldsAch });
                    }
                    this.#modalRef?.current?.open(
                        <SdkModals
                            modals={additionalInfo.modals}
                            popupRef={this.#popupRef as PopupRef}
                            typeOfTransfer={this.#typeOfTransfer}
                            modalRef={this.#modalRef}
                            submitRef={this.#submitRef}
                            paymentMethod={paymentMethod}
                            changePaymentMethodPlaid={(code: string, f: unknown) => {
                                this.#paymentMethodService.current?.changePaymentMethod(PAYMENT_MODULR, code, f);
                            }}
                            onClosePlaidModal={() => this.changePaymentMethodOnPrecheckClose(extra.precheck, availablePaymentMethodsList)}
                        />,
                    );
                } else if (
                    !!extra?.precheck &&
                    !additionalInfo.onlyCVV &&
                    !additionalInfo.hasSdkModal &&
                    ((currentScreen === RECIPIENT_ADDITIONAL_INFO_SCREEN && !additionalInfo.hasTransferAdditionalFields) ||
                        (currentScreen === SENDER_ADDITIONAL_INFO_SCREEN &&
                            !additionalInfo.hasRecipientAdditionalFields &&
                            !additionalInfo.hasTransferAdditionalFields))
                ) {
                    this.#sendNextWithCardModalIfNeeded(currentScreen, props, json.messages, extra, availablePaymentMethodsList, paymentMethodState);
                } else if (isPreCheck) {
                    this.#sendNextWithCardModalIfNeeded(currentScreen, props, json.messages, extra, availablePaymentMethodsList, paymentMethodState);
                } else {
                    switchToNextScreen();
                }

                if (!keepSpinner) props.toggleSpinner(false);
            })
            .finally(() => props.toggleSpinner(false))
            .catch((err) => {
                logWarn(err);
                props.toggleSpinner(false);
            });
    }

    #sendNextWithCardModalIfNeeded(
        currentScreen: string,
        props: ScreenValidationProps,
        messages: Record<string, string> | undefined,
        extra: ApiExtra,
        availablePaymentMethodsList: PaymentMethodsList,
        paymentMethodState: unknown,
    ): void {
        if (!this.#isBonusWithdrawFlow) {
            if (currentScreen === CARD_EXTRA_FIELDS_SCREEN && extra.cardAddedSuccessfully) {
                props.toggleModalWithTimer(true, messages?.successMessage, () => {
                    this.#sendParamToStateMachine({ type: 'NEXT' });
                    props.toggleModalWithTimer(false, '', false);
                    if (extra.precheck) {
                        this.showPrecheck(currentScreen, extra.precheck, '', undefined, availablePaymentMethodsList, paymentMethodState);
                    }
                });
                return;
            }

            if (extra.precheck) {
                this.showPrecheck(currentScreen, extra.precheck, '', undefined, availablePaymentMethodsList, paymentMethodState);
            }
            this.#sendParamToStateMachine({ type: 'NEXT' });
        }
    }

    #checkMessageBodyMessages(json: ApiResponse = {}): void {
        if (json?.messageBodyMessages && Array.isArray(json?.messageBodyMessages) && json?.messageBodyMessages.length > 0) {
            this.#updateMessages!(json.messageBodyMessages);
            const hasMessageBodyError = json.messageBodyMessages.every((message) => message?.messageType === ERROR_TOAST || message?.messageType === ERROR);
            if (hasMessageBodyError) {
                throw new Error('Message Body messages Error');
            }
        }
    }

    updateFieldsForShowGlobalFlow(formName: string, isGlobalFlow: boolean, isRepeat: boolean, fields: FieldsMap, fieldsForShow: string[]): void {
        if (isRepeat && fields && (isGlobalFlow || DELIVERY_SWIFT === fields[DELIVERY_METHOD]?.value)) {
            this.#fieldsForShow = fieldsForShow;
            const accountFieldName = Object.values(fields)
                .filter((field) => field?.visible)
                .filter((field) => {
                    const name = field.name;
                    return name === TO_BIC || name === SWIFT_CODE || name === TO_ISSUER_CODE || name === BANK_ACCOUNTCODE || name === BANK_ACCOUNT_CODE;
                })[0];
            if (accountFieldName) {
                const readOnlyFields = [accountFieldName, fields[TO_ACCOUNT_TYPE]];
                readOnlyFields.forEach((field) => {
                    this.#changeFieldProperty!(formName, field.name ?? '', 'isReadOnly', true);
                    if (field.name === TO_BIC) {
                        this.#changeFieldProperty!(formName, TO_BIC, 'visible', false);
                    }
                });
            }
        }
    }

    #updateReduxForGlobalFlow(json: ApiResponse, fields: FieldsMap, fieldsForShow: string[], formName: string, typeOfTransfer: string): void {
        const updatedFields: FieldsMap = {};
        this.#fieldsForShow = fieldsForShow;
        let fieldMappings: Record<string, unknown> = {};
        const accountFieldsNames: string[] = [TO_BIC, SWIFT_CODE, TO_ISSUER_CODE, BANK_ACCOUNTCODE, BANK_ACCOUNT_CODE];

        Object.keys(fields).map((key) => {
            const field = fields[key];
            const order = typeof field.order === 'number' ? field.order : 0;
            if (field.visible && order > 1000 && field.value != null && field.value !== '') {
                field.visible = false;
                if (field.name) {
                    updatedFields[field.name] = field;
                }
            }
        });

        if (typeOfTransfer === RECIPIENT_TYPE_SELF) {
            fieldMappings = {
                [TO_NAME]: json.extra?.profile?.userName,
                [TO_NAME_F]: json.extra?.profile?.userLastName,
                [TO_RECIPIENT]: json.extra?.profile?.fullName,
            };
        }
        fieldsForShow.forEach((fieldName) => {
            const currentField = fields[fieldName];
            if (currentField) {
                currentField.visible = true;
                if (TO_IBAN === fieldName) {
                    const isIbanField = fields[IS_IBAN];
                    if (isIbanField) {
                        isIbanField.value = 1;
                    }
                    updatedFields[IS_IBAN] = isIbanField;
                } else if (typeOfTransfer === RECIPIENT_TYPE_SELF && (fieldName === TO_RECIPIENT || fieldName === TO_NAME || fieldName === TO_NAME_F)) {
                    const field = fields[fieldName];

                    field.value = fieldMappings[fieldName] as FieldValue;
                    field.isReadOnly = true;
                    field.commentLong = '';
                    field.comment = '';
                    updatedFields[fieldName] = field;
                }
            }
        });

        const accountField = Object.values(fields).find((field) => field?.visible && accountFieldsNames.includes(field.name ?? ''));

        if (accountField) {
            accountField.isReadOnly = true;
            if (accountField.name) {
                updatedFields[accountField.name] = accountField;
            }

            const toAccountType = fields[TO_ACCOUNT_TYPE];
            toAccountType.isReadOnly = true;
            updatedFields[TO_ACCOUNT_TYPE] = toAccountType;
        }

        if (json?.extra?.toCurrency?.id) {
            const amountToCcy = fields[AMOUNT_TO_CCY];
            amountToCcy.value = json?.extra?.toCurrency?.id;
            updatedFields[AMOUNT_TO_CCY] = amountToCcy;
        }

        const fieldsToRemove: string[] = [];
        const toCountryId = json.extra?.toCountryId;
        let filteredFields = Object.values(fields).filter(
            (field) => !field.visible && field.destination === 'TO' && (field.value === null || field.value === undefined),
        );
        if (toCountryId?.name) {
            filteredFields = filteredFields.filter((field) => field.name !== toCountryId.name);
            const amountToCcy = fields[toCountryId.name];
            if (amountToCcy) {
                amountToCcy.value = toCountryId.value;
                updatedFields[toCountryId.name] = amountToCcy;
            }
        }
        filteredFields.forEach((field) => field.name && fieldsToRemove.push(field.name));
        this.#removeFields!(formName, fieldsToRemove);

        if (updatedFields) {
            this.#updateFields!(formName, updatedFields);
        }

        if (json.extra) {
            void this.#addExtras!(json.extra);
        }
    }

    markCurrencyChange(): void {
        this.#isCurrencyChange = true;
    }

    setNextMachineScreen(currentScreen: string, infoForAdditionalScreens: AdditionalInfo): void {
        const {
            isShowSenderAdditionalScreen,
            isShowVisibleSenderAdditionalFields,
            isShowCardExtraFields,
            hasRecipientAdditionalFields,
            hasTransferAdditionalFields,
            onlyCVV,
        } = infoForAdditionalScreens;

        if (CALCULATOR_SCREEN === currentScreen && this.#isCurrencyChange) {
            this.#isCurrencyChange = false;
            return;
        }
        if (PAYMENT_METHOD_SCREEN === currentScreen) {
            this.#sendParamToStateMachine({
                type: 'NEXT_WITH_UPDATE_CONTEXT',
                data: {
                    hasCardExtraFields: isShowCardExtraFields && !onlyCVV,
                    hasSenderAdditionalFields: isShowSenderAdditionalScreen && isShowVisibleSenderAdditionalFields,
                    hasRecipientAdditionalFields: hasRecipientAdditionalFields,
                    hasTransferAdditionalFields: hasTransferAdditionalFields,
                },
            });
        } else {
            this.#sendParamToStateMachine({ type: 'NEXT' });
        }
        window.history.pushState({ index: ++this.#historyIndex, state: 'fake-route' }, '', window.location.href);
    }

    viewRateFirstBackFlowForMachine(e: React.SyntheticEvent, currentScreen: string, resetViewRatesFirst: () => void): void {
        if (CALCULATOR_SCREEN === currentScreen) {
            e.preventDefault();
            this.#sendParamToStateMachine({ type: 'BACK' });
            resetViewRatesFirst();
        } else if (RECIPIENT_MAIN_INFO_SCREEN === currentScreen) {
            e.preventDefault();
            this.#sendParamToStateMachine({ type: 'BACK' });
        }
    }
    //implement it for send screen machine
    #canSkipPaymentMethodScreen(extra: ApiExtra = {}, dcSources: Record<string, Array<unknown>> = {}): boolean {
        try {
            const categories: Record<string, { method?: string }> = {};
            const availablePaymentMethodsList: PaymentMethodsList = extra?.availablePaymentMethodsList ?? {};
            const sourceOptionsGroups = extra?.paymentMethodsOrder?.sourceOptionsGroups;

            if (Object.keys(availablePaymentMethodsList).length > 0 && sourceOptionsGroups && Object.keys(sourceOptionsGroups).length > 0) {
                for (const group of Object.values(sourceOptionsGroups)) {
                    for (const item of group?.paymentOptionsItems ?? []) {
                        if (item?.category && Object.hasOwn(availablePaymentMethodsList, item.category) && availablePaymentMethodsList[item.category]) {
                            categories[item.category] = availablePaymentMethodsList[item.category];
                        }
                    }
                }
            }
            const noSources = Object.keys(dcSources).length === 0 || Object.values(dcSources).every((sourcesArr) => sourcesArr.length === 0);
            const noExternalMethodsOnlyBankCard = Object.values(categories).every((category) => category.method === PAYMENT_BANK_CARD);
            return noSources && noExternalMethodsOnlyBankCard;
        } catch (e) {
            Sentry.captureException(e, {
                level: 'error',
            });
            logWarn(e);
            return false;
        }
    }

    #showScreenWithErrors(fields: FieldsMap, errorFields: ApiErrors, currentScreen: string): void {
        this.#popupRef?.current?.close();
        if (fields[SOURCE_CARD_CVV]) {
            this.#changeFormFieldValue!(SEND_FORM_NAME, null, SOURCE_CARD_CVV);
        }
        goToScreenWithError(this.#sendParamToStateMachine, fields, errorFields, currentScreen, this.#isBonusWithdrawFlow);
    }

    #submitHandler(
        cards: Array<{ code?: string; alias?: string; description?: string }>,
        fields: FieldsMap,
        messages: Record<string, string>,
        json: ApiResponse,
        currentScreen: string,
        isRegular: boolean,
        toCountry: { nameEn?: string } | string | null | undefined,
        getPaymentFlow: () => string,
        availablePaymentMethodsList: PaymentMethodsList = {},
        paymentMethodState: unknown,
    ): void {
        const { cvvPopupTitle } = messages;
        void this.#updateFormProperties!(SEND_FORM_NAME, `${AUTH_SEND_PRECHECK}${this.#sendUriPostfix}`, true);
        if (json) {
            this.#checkMessageBodyMessages(json);
            const form = json.forms?.[SEND_FORM_NAME];
            const responseFields = form?.fields;
            const extra: ApiExtra = json.extra ?? {};
            const paymentMethodValue = responseFields?.[PAYMENT_METHOD]?.value;
            const paymentMethod = typeof paymentMethodValue === 'string' ? paymentMethodValue : '';
            const additionalInfo = getAdditionalInfo(responseFields, extra);
            const isShowAdditionalScreens = additionalInfo.isShow;
            const isShowSdkModal = additionalInfo.hasSdkModal;
            const isPreCheck = !!extra?.precheck && !extra?.flink && (additionalInfo.onlyCVV || !isShowAdditionalScreens);
            const isDisclaimer = Boolean(extra?.isDisclaimerFromServerSide);
            if (json?.extra) {
                void this.#addExtras!(json.extra);
            }
            if (json.errors && Object.keys(json.errors).length > 0) {
                this.#showScreenWithErrors(responseFields ?? fields, json.errors, currentScreen);
                this.#scrollToField!(SEND_FORM_NAME, Object.keys(json.errors)[0]);
            } else if (isDisclaimer) {
                if (responseFields) {
                    void this.#setNewFields!(SEND_FORM_NAME, responseFields);
                }
                this.#toggleModal!(
                    true,
                    <Disclaimer
                        formName={SEND_FORM_NAME}
                        clickAgree={(e: React.SyntheticEvent) => {
                            this.#agreeWithDisclaimer(e, true);
                        }}
                        recipientType={this.#typeOfTransfer}
                    />,
                    true,
                    false,
                );
            } else if (isShowSdkModal && !isShowAdditionalScreens) {
                if (additionalInfo.modals.isAch) {
                    const fieldsAch: FieldsMap = extra?.aml_level_form?.fields ?? {};
                    const jsonFields = responseFields ?? {};
                    void this.#setNewFields!(SEND_FORM_NAME, { ...jsonFields, ...fieldsAch });
                }
                this.#modalRef?.current?.open(
                    <SdkModals
                        modals={additionalInfo.modals}
                        popupRef={this.#popupRef as PopupRef}
                        typeOfTransfer={this.#typeOfTransfer}
                        modalRef={this.#modalRef}
                        submitRef={this.#submitRef}
                        paymentMethod={paymentMethod}
                        changePaymentMethodPlaid={(code: string, f: unknown) => {
                            this.#paymentMethodService.current?.changePaymentMethod(PAYMENT_MODULR, code, f);
                        }}
                        onClosePlaidModal={() => this.changePaymentMethodOnPrecheckClose(extra.precheck, availablePaymentMethodsList)}
                    />,
                );
            } else if (extra?.showPaymentMethodRestriction) {
                this.#popupRef?.current?.open(
                    <SimplePopup
                        text={<DangerouslyHtml dangerouslyHtml={json.messages?.paymentMethodErrorText} />}
                        popupRef={this.#popupRef}
                        handleClose={() => {
                            this.#popupRef?.current?.close();
                        }}
                    >
                        <PopupButton
                            iconName="retry"
                            text={json.messages?.changePaymentOption}
                            onClick={() => {
                                this.#popupRef?.current?.close();
                            }}
                        />
                        <PopupButton
                            iconName="arrow-back"
                            text={json.messages?.changeAmount}
                            onClick={(event) => {
                                event.preventDefault();
                                this.#sendParamToStateMachine({ type: 'BACK' });
                                this.#popupRef?.current?.close();
                            }}
                        />
                        {extra.increaseLimit && (
                            <PopupButton
                                iconName={'arrow-up'}
                                text={json.messages?.calcCommissionIncreaseLimit}
                                onClick={() => {
                                    void this.#toggleSpinner!(true);
                                    this.#popupRef?.current?.close();
                                    this.#toggleModal!(
                                        true,
                                        <ModalComponent canCloseModal={false} isVerticalCentered>
                                            <InfoPage
                                                insideModalComponent={true}
                                                iconName={'id-card-80'}
                                                iconColor="primary"
                                                headerText={<DangerouslyHtml dangerouslyHtml={json.messages?.screenTitle} />}
                                                secondaryText={json.messages?.screenText}
                                                topButtonType="link"
                                                topButtonCaption={json.messages?.notNowButton}
                                                topButtonOnClick={(e: React.SyntheticEvent) => {
                                                    e.preventDefault();
                                                    this.#toggleModal!(false);
                                                }}
                                                primaryButtonCaption={json.messages?.nextButton}
                                                primaryButtonType="link"
                                                primaryButtonLink={extra.aml_gt_increase_level_link}
                                            />
                                        </ModalComponent>,
                                    );
                                    void this.#toggleSpinner!(false);
                                }}
                            />
                        )}
                        {extra?.canIncreaseLimit && <PopupButton iconName="arrow-up" text={json.messages?.upgradeLimitButton} href={extra.increaseLimit} />}
                    </SimplePopup>,
                );
            } else if (isPreCheck) {
                this.showPrecheck(currentScreen, extra.precheck, toCountry, getPaymentFlow, availablePaymentMethodsList, paymentMethodState);
            } else if (responseFields) {
                if (this.#isGlobalFlow) {
                    const toBic = responseFields[TO_BIC];
                    if (toBic && DELIVERY_SWIFT === responseFields[DELIVERY_METHOD]?.value) {
                        toBic.isReadOnly = true;
                    }
                }
                const fieldsAch: FieldsMap = additionalInfo.modals.isAch && extra?.aml_level_form?.fields ? extra.aml_level_form.fields : {};
                void this.#setNewFields!(SEND_FORM_NAME, { ...responseFields, ...fieldsAch }).then(() => {
                    this.#validateResponse(cards, extra, currentScreen, isRegular, responseFields, cvvPopupTitle, additionalInfo, json, getPaymentFlow);
                });
            }
        }
    }

    showPrecheck(
        currentScreen: string,
        precheck: ApiPrecheck | undefined,
        toCountry: { nameEn?: string } | string | null | undefined,
        getPaymentFlow?: () => string,
        availablePaymentMethodsList: PaymentMethodsList = {},
        paymentMethodState: unknown = undefined,
    ): void {
        if (this.#shouldGateOnQuestionnaire()) {
            const resumePrecheck = (): void => {
                this.#openPrecheckPopup(currentScreen, precheck, toCountry, getPaymentFlow, availablePaymentMethodsList, paymentMethodState);
            };
            void this.#toggleSpinner!(false);
            this.#modalRef?.current?.open(<OnboardingQuestionnaireModal modalRef={this.#modalRef} onSubmitSuccess={resumePrecheck} />);
            return;
        }
        this.#openPrecheckPopup(currentScreen, precheck, toCountry, getPaymentFlow, availablePaymentMethodsList, paymentMethodState);
    }

    #shouldGateOnQuestionnaire(): boolean {
        if (this.#isBonusWithdrawFlow) {
            return false;
        }
        const extra = this.#extra;
        const questionnaire = extra?.questionnaire as { fields?: unknown[] } | undefined;
        return Boolean(extra?.useQuestionnaireScreen) && Array.isArray(questionnaire?.fields) && (questionnaire?.fields?.length ?? 0) > 0;
    }

    #openPrecheckPopup(
        currentScreen: string,
        precheck: ApiPrecheck | undefined,
        toCountry: { nameEn?: string } | string | null | undefined,
        getPaymentFlow: (() => string) | undefined,
        availablePaymentMethodsList: PaymentMethodsList,
        paymentMethodState: unknown,
    ): void {
        if (precheck && currentScreen === PAYMENT_METHOD_SCREEN) {
            const amplitudeDeliveryMethod = precheck?.deliveryMethod
                ? transactionMethods[precheck.deliveryMethod as keyof typeof transactionMethods]
                : undefined;
            const amplitudeSendingMethod = precheck?.paymentMethod ? transactionMethods[precheck.paymentMethod as keyof typeof transactionMethods] : undefined;
            const payoutCountry = typeof toCountry === 'string' ? toCountry : toCountry?.nameEn;
            const paymentFlow = getPaymentFlow ? getPaymentFlow() : '';
            amplitudeInstance.getInstance().logEvent(NEW_EXTERNAL_CARD_ADDED, {
                [PAYOUT_COUNTRY]: payoutCountry,
                [PAYOUT_METHOD]: amplitudeDeliveryMethod,
                [PAYMENT_METHOD_AMPLITUDE]: amplitudeSendingMethod,
                [PAYMENT_FLOW]: paymentFlow,
            });
        }

        void this.#toggleSpinner!(false);
        this.#popupRef?.current?.open(
            <PreCheckPopup
                popupRef={this.#popupRef}
                formName={SEND_FORM_NAME}
                submitRef={this.#submitRef}
                modalRef={this.#modalRef as ModalRef}
                onOverlayClick={() => {
                    ApiService.fetchWithoutSpinner(`${AUTH_CANCEL_TRANSACTION}?paymentId=${precheck?.id ?? ''}`);
                    this.changePaymentMethodOnPrecheckClose(precheck, availablePaymentMethodsList);
                }}
                changePaymentMethod={() => this.changePaymentMethodOnPrecheckClose(precheck, availablePaymentMethodsList)}
                paymentMethodState={paymentMethodState}
            />,
        );
    }

    changePaymentMethodOnPrecheckClose(precheck: ApiPrecheck | undefined, availablePaymentMethodsList: PaymentMethodsList): void {
        if (!this.#isBonusWithdrawFlow) {
            const paymentMethods = Object.values(availablePaymentMethodsList);
            const actualPaymentMethod =
                paymentMethods.find((method) => method?.lastSuccess) ??
                paymentMethods.find((method) => method?.defaultPaymentSource) ??
                paymentMethods.find((method) => method?.method === PAYMENT_BANK_CARD);
            if (actualPaymentMethod) {
                const resolvedMethod = actualPaymentMethod.method ?? '';
                const resolvedCode = actualPaymentMethod.code ?? '';
                this.#paymentMethodService.current?.changePaymentMethod(resolvedMethod, resolvedCode, null);
            }
        }
    }

    #validateResponseForMachine(
        cards: Array<{ code?: string; alias?: string; description?: string }>,
        extra: ApiExtra,
        currentScreen: string,
        _isRegular: boolean,
        responseFields: FieldsMap,
        cvvPopupTitle: string,
        additionalInfo: AdditionalInfo,
        json: ApiResponse,
        getPaymentFlow: () => string,
    ): void {
        const paymentMethod = responseFields[PAYMENT_METHOD]?.value;
        const sourceCodeFromValue = responseFields[SOURCE_CODE_FROM]?.value;
        const sourceCodeFrom = typeof sourceCodeFromValue === 'string' ? sourceCodeFromValue : undefined;
        const { senderAdditionalOrCardsExtraFields, hasRecipientAdditionalFields, hasSenderAdditionalOrCardsExtraFields, hasTransferAdditionalFields } =
            additionalInfo;
        const onlyCvv =
            (paymentMethod === PAYMENT_BANK_CARD ||
                paymentMethod === PAYMENT_BANK_CARD_110 ||
                paymentMethod === PAYMENT_DEBIT_BANK_CARD_111 ||
                paymentMethod === PAYMENT_CREDIT_BANK_CARD_112) &&
            getExtra(sourceCodeFrom, 'type') === DC &&
            hasSenderAdditionalOrCardsExtraFields &&
            senderAdditionalOrCardsExtraFields.length === 1 &&
            !hasRecipientAdditionalFields &&
            !hasTransferAdditionalFields;
        if (currentScreen !== CALCULATOR_SCREEN && onlyCvv) {
            this.toggleCvvPopup(cards, cvvPopupTitle, String(sourceCodeFrom ?? ''), json?.app?.messageBody, json?.errors ?? {}, getPaymentFlow);
        } else {
            this.setNextMachineScreen(currentScreen, additionalInfo);
        }
    }

    #validateResponse(
        cards: Array<{ code?: string; alias?: string; description?: string }>,
        extra: ApiExtra,
        currentScreen: string,
        isRegular: boolean,
        responseFields: FieldsMap,
        cvvPopupTitle: string,
        additionalInfo: AdditionalInfo,
        json: ApiResponse,
        getPaymentFlow: () => string,
    ): void {
        this.#validateResponseForMachine(cards, extra, currentScreen, isRegular, responseFields, cvvPopupTitle, additionalInfo, json, getPaymentFlow);
    }
}
